import type { DateRange, TimestampInMillis, UUID, Vehicle, VehicleDTO, VehicleGroupDTO, VehicleSize } from '@cobliteam/common-types';
import type { VehicleTag } from '../../__generated__/graphql';

export interface MaintenanceOption {
  key?: UUID;
  value: string;
}

export type MaintenanceType = 'corrective' | 'preventive';

export interface MaintenanceItem {
  id: UUID;
  name: MaintenanceOption;
  price?: number;
  remark?: string;
}

export interface PastMaintenance {
  id: UUID;
  vehicleId: UUID;
  date: TimestampInMillis;
  odometerInMeters: number;
  types: MaintenanceType[];
  periodicitiesInMeters?: number[];
  totalCost?: number;
  items?: MaintenanceItem[];
  generalObservations?: string;
  recurrenceNames?: { [recurrenceValue: number]: string };
  periodFilterApplied?: DateRange;
}

export interface PastMaintenanceWithVehicle extends PastMaintenance {
  vehicle: Vehicle;
}

export interface RegisterPastMaintenance extends Omit<PastMaintenance, 'id'> {
  id?: UUID;
}

export interface PastMaintenancesMaps {
  pastMaintenancesById: { [maintenanceId: UUID]: PastMaintenance };
  pastMaintenancesIdsByVehicleId: { [vehicleId: UUID]: UUID[] };
}

export type ReminderType = 'delayed' | 'warning' | 'onTime';

export interface UpcomingMaintenance {
  lastRevisionDateInMillis?: TimestampInMillis;
  revisionPeriodicityInMeters: number;
  metersDrivenSinceLastRevision: number;
  createdAt: TimestampInMillis;
  revisionName?: string;
  type: ReminderType;
}

export interface UpcomingMaintenanceByVehicleId {
  [vehicleId: UUID]: UpcomingMaintenance[];
}

export interface UpcomingMaintenancesByStatus {
  delayed: UpcomingMaintenanceByVehicleId;
  warning: UpcomingMaintenanceByVehicleId;
  onTime: UpcomingMaintenanceByVehicleId;
}

export interface Recurrence {
  recurrenceInKm: number;
  recurrenceName?: string;
}

export interface VehicleMaintenanceRemindersSetup {
  vehicleId: UUID;
  lastMaintenanceDate?: TimestampInMillis;
  odometerInMetersAtThatDate?: number;
  currentOdometerInMeters: number;
}

export interface MaintenanceRemindersSetup {
  vehiclesMaintenanceSetup: VehicleMaintenanceRemindersSetup[];
  recurrenceInMeters: number[];
  recurrenceNames: { [value: number]: string };
}

export interface ReminderPrefill {
  vehicleId: UUID;
  recurrences: {
    [periodicity: string]: {
      checked: boolean;
      name?: string;
    };
  };
  createdAt: TimestampInMillis;
}

export interface VehicleOdometerSetup {
  odometerInKm?: number;
  odometerDateAt: number;
}

export interface BaseMaintenanceHistoryRequest {
  startDate: string;
  endDate: string;
  driverIds?: string[];
  vehicleIds?: string[];
  vehicleGroupIds?: string[];
  vehicleSizes?: VehicleSize[];
  productTypes?: string[];
  tagNames?: string[];
  searchQuery?: string;
  sortColumn?: string;
  sortOrder?: string;
}

export interface Part {
  id: UUID;
  description: string;
}

export interface Parts {
  id: UUID;
  price: number;
  quantity: number;
  description: string;
  part: Part;
}

export interface MaintenanceRecurrence {
  recurrenceInKms: number;
  everyKms: string;
}

export interface MaintenanceRow {
  id: UUID;
  types: MaintenanceType[];
  occurrenceAt: string;
  updatedAt: string;
  createdBy: UUID;
  vehicle?: MaintenanceVehicleDTO;
  totalPrice: number;
  odometerInKms: number;
  description: string;
  parts: Parts[];
  recurrences: MaintenanceRecurrence[];
  apiVersion?: string;
}

export interface BaseMaintenanceRequest {
  startDate: string;
  endDate: string;
  maintenanceTypes?: string[];
  vehicleIds?: string[];
  vehicleGroupIds?: string[];
  vehicleSizes?: VehicleSize[];
  tagNames?: string[];
  searchQuery?: string;
  sortColumn?: string;
  sortOrder?: string;
}

export interface MaintenanceReportRequest extends BaseMaintenanceRequest {
  report_type: MaintenanceReportType;
}

export enum MaintenanceReportType {
  AGGREGATED = 'AGGREGATED',
  DETAILED = 'DETAILED',
}

export enum MaintenanceReminderStatus {
  ON_TIME = 'ON_TIME',
  WARNING = 'WARNING',
  DELAYED = 'DELAYED',
}

export interface MaintenanceReminderCreateRequest {
  vehicleId: UUID;
  description: string;
  recurrenceInKm: number;
  lastMaintenanceDate?: string;
  lastMaintenanceOdometerInKm?: number;
}

export interface MaintenanceReminderListRequest {
  status?: MaintenanceReminderStatus;
}

export interface MaintenanceReminderEditRequest {
  title: {
    id?: string;
    value?: string;
  };
  description: string;
  recurrenceInKm: number;
}

export interface MaintenanceStatus {
  name: MaintenanceReminderStatus;
  km: number;
}

export enum MaintenanceReminderType {
  ODOMETER = 'ODOMETER',
  DISTANCE = 'DISTANCE',
  DATE = 'DATE',
  DATE_AND_ODOMETER = 'DATE_AND_ODOMETER',
  DATE_AND_DISTANCE = 'DATE_AND_DISTANCE',
}

export interface MaintenanceReminderRow {
  id: UUID;
  vehicle: MaintenanceVehicleDTO;
  description: string;
  recurrenceInKm: number;
  nextOccurrenceInKm: number;
  lastMaintenanceDate?: string;
  status: MaintenanceStatus;
  type: MaintenanceReminderType;
  apiVersion: string;
  title?: string;
}

export interface MaintenanceVehicleDTO extends VehicleDTO {
  brand?: string;
  size?: VehicleSize;
  group?: VehicleGroupDTO;
  tags?: VehicleTag[];
}

export interface MaintenanceReminderResponse {
  count: number;
  rows: MaintenanceReminderRow[];
}
export interface MaintenanceCostItem {
  date: string;
  preventive: number;
  corrective: number;
}

export interface MaintenanceCost {
  vehicleId: UUID;
  costs: MaintenanceCostItem[];
  totalCost: number;
}

interface RowsWithCount<T> {
  count: number;
  rows: T[];
}

export interface MaintenanceReminderResponse extends RowsWithCount<MaintenanceReminderRow> {}

export interface MaintenanceCosts extends RowsWithCount<MaintenanceCost> {}
